import {
  put, takeLatest, call,
} from 'redux-saga/effects';
import { notification } from 'antd';
import TrackerActions from '@Actions/trackerActions';
import CommonServiceMiddleware from '../services/CommonServiceMiddleware';

// Define action types
const createActionTypes = (base) => ({
  REQUEST: `${base}_REQUEST`,
  SUCCESS: `${base}_SUCCESS`,
  FAILURE: `${base}_FAILURE`,
  RESET: `${base}_RESET`,
  LOADING: `${base}_LOADING`,
});

// Define action creators
const createActionCreators = (type) => ({
  request: (payload, callback) => ({ type: type.REQUEST, payload, callback }),
  success: (payload) => ({ type: type.SUCCESS, payload }),
  failure: (error) => ({ type: type.FAILURE, error }),
  reset: () => ({ type: type.RESET }),
  loading: (isLoading) => ({ type: type.LOADING, isLoading }),
});

// Create initial state and reducer
const createReducer = (type) => {
  const initialState = {
    loading: false,
    data: null,
    error: null,
  };

  return (state = initialState, action) => {
    switch (action.type) {
      case type.REQUEST:
        return { ...state, loading: true, error: null };
      case type.SUCCESS:
        return {
          ...state, loading: false, data: action.payload, error: null,
        };
      case type.FAILURE:
        return { ...state, loading: false, error: action.error };
      case type.RESET:
        return initialState;
      case type.LOADING:
        return { ...state, loading: action.isLoading };
      default:
        return state;
    }
  };
};

// Define a saga to handle the API calls
const createSaga = ({
  type, apiDetails, customHandler, errors = { defaultError: true, customError: '' }, enableNotification, runCallbackOnError, customNotificationSuccessMessage,
}) => {
  const commonService = new CommonServiceMiddleware();
  const removeEmptyKeys = (obj) => {
    return Object.fromEntries(
      Object.entries(obj).filter(([_, value]) =>
        value !== null && value !== undefined && value !== ''
      )
    );
  };

  function* handleRequest(action) {
    try {
      yield put({ type: type.LOADING, isLoading: true });
      if (customHandler?.before) yield customHandler.before(action);
      const response = yield call(commonService.callAPI, {
        url: action.payload?.url || apiDetails.url,
        method: apiDetails.method,
        body: action.payload?.body,
        query: action.payload?.query ? removeEmptyKeys(action.payload?.query) : action.payload?.query,
        file: action.payload?.file,
      });
      if (response?.success) {
        yield put({ type: type.SUCCESS, payload: (response?.data || response) });
        yield put(TrackerActions.trackAction(type.SUCCESS, (response?.data || response)));
        if (enableNotification && response.message) {
          notification.open({
            type: 'success',
            message: response.message,
            duration: 4,
            placement: 'top',
          });
        } if (enableNotification && customNotificationSuccessMessage) {
          notification.open({
            type: 'success',
            message: customNotificationSuccessMessage,
            duration: 4,
            placement: 'top',
          });
        }
        if (action?.callback) action.callback(response);
      } else if (errors?.defaultError === true) {
        if (runCallbackOnError && action?.callback) action.callback(response);
        if (enableNotification && response.message) {
          notification.open({
            type: 'error',
            message: response.message,
            duration: 4,
            placement: 'top',
          });
        } else {
          throw new Error(response?.message);
        }
      } else if (errors?.customError !== '') {
        yield put({ type: type.FAILURE, error: errors?.customError });
      } else {
        yield put({ type: type.FAILURE, error: response?.data?.error });
      }

      // if (action?.callback) action.callback(response);
      if (customHandler?.afterSuccess) yield customHandler.afterSuccess(response);
    } catch (error) {
      yield put({ type: type.FAILURE, error });
      if (customHandler?.onError) yield customHandler.onError(error);
      yield put(TrackerActions.trackAction(type.FAILURE, error));
    } finally {
      yield put({ type: type.LOADING, isLoading: false });
      if (customHandler?.finally) yield customHandler.finally(action);
    }
  }

  return function* watchSaga() {
    yield takeLatest(type.REQUEST, handleRequest);
  };
};

// Main utility function
const createModule = ({
  base, apiDetails, customHandler, errors, enableNotification, runCallbackOnError, customNotificationSuccessMessage,
}) => {
  const types = createActionTypes(base);
  const actions = createActionCreators(types);
  const reducer = createReducer(types);
  const saga = createSaga({
    type: types, apiDetails, customHandler, errors, enableNotification, runCallbackOnError, customNotificationSuccessMessage,
  });

  return {
    types, actions, reducer, saga,
  };
};

// Example usage
// const GET_KEYBOARD_SHORTCUTS = createModule(
//   'GET_KEYBOARD_SHORTCUTS',
//   {
//     url: '/api/keyboard-shortcuts',
//     method: 'get',
//   },
//   {
//     * before(action) {
//       yield put(Actions.GET_KEYBOARD_SHORTCUTS.loading(true));
//     },
//     * onError(error) {
//       notification.open({
//         type: 'error',
//         message: error.message,
//         duration: 4,
//         placement: 'top',
//       });
//     },
//     * finally() {
//       yield put(Actions.GET_KEYBOARD_SHORTCUTS.loading(false));
//     },
//   },
// );

// Add GET_KEYBOARD_SHORTCUTS.reducer to your root reducer
// Add GET_KEYBOARD_SHORTCUTS.saga to your root saga

export { createModule };
